import type { ProductImportRow } from "@repo/shared/types";
import Papa from "papaparse";
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useProductImportJob, useStartProductImport } from "@/lib/hooks/use-portfolio-collections";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./import-csv-dialog.i18n";

type ImportCsvDialogProps = {
  open: boolean;
  portfolioCollectionId: string;
  onClose: () => void;
};

type ParseResult = { rows: ProductImportRow[]; error: string | null };

function parseCsv(text: string): ParseResult {
  const { data, errors } = Papa.parse<string[]>(text, {
    delimitersToGuess: [";", ","],
    skipEmptyLines: "greedy",
  });

  if (errors.length > 0) {
    const first = errors[0];
    return { rows: [], error: `Line ${(first.row ?? 0) + 1}: ${first.message}` };
  }

  const rows: ProductImportRow[] = [];
  for (let i = 0; i < data.length; i++) {
    const parts = data[i].map((p) => p.trim());

    if (
      i === 0 &&
      parts.length === 2 &&
      parts[0]?.toLowerCase() === "source" &&
      parts[1]?.toLowerCase() === "id"
    ) {
      continue;
    }

    if (parts.length !== 2 || !parts[0] || !parts[1]) {
      return {
        rows: [],
        error: `Line ${i + 1}: expected exactly two non-empty fields (source,id)`,
      };
    }
    rows.push({ source: parts[0], id: parts[1] });
  }

  if (rows.length === 0) {
    return { rows: [], error: "CSV contained no data rows" };
  }
  return { rows, error: null };
}

export function ImportCsvDialog({ open, portfolioCollectionId, onClose }: ImportCsvDialogProps) {
  const t = useT(T);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [parsedRows, setParsedRows] = useState<ProductImportRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);

  const startImport = useStartProductImport(portfolioCollectionId);
  const { job } = useProductImportJob(portfolioCollectionId, jobId);

  const reset = () => {
    setParsedRows([]);
    setParseError(null);
    setSubmitError(null);
    setJobId(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParseError(null);
    const text = await file.text();
    const { rows, error } = parseCsv(text);
    setParsedRows(rows);
    setParseError(error);
  };

  const handleSubmit = () => {
    if (parsedRows.length === 0) return;
    setSubmitError(null);
    startImport.mutate(
      { rows: parsedRows },
      {
        onSuccess: (resp) => setJobId(resp.jobId),
        onError: (err) => setSubmitError(err instanceof Error ? err.message : String(err)),
      },
    );
  };

  const finished = job?.status === "done" || job?.status === "failed";
  const showProgress = jobId !== null;

  const statusLabel = (s: string) => {
    switch (s) {
      case "pending":
        return t.statusPending;
      case "processing":
        return t.statusProcessing;
      case "success":
        return t.statusSuccess;
      case "failed":
        return t.statusFailed;
      case "skipped":
        return t.statusSkipped;
      case "done":
        return t.statusDone;
      default:
        return s;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t.title}</DialogTitle>
          <DialogDescription>{t.description}</DialogDescription>
        </DialogHeader>

        {!showProgress && (
          <div className="space-y-4">
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFile}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={startImport.isPending}
              >
                {t.chooseFile}
              </Button>
            </div>

            {parseError && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {t.parseError}: {parseError}
              </div>
            )}

            {submitError && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {submitError}
              </div>
            )}

            {parsedRows.length > 0 && (
              <div className="space-y-2">
                <div className="text-sm text-muted-foreground">
                  {parsedRows.length} {t.rowsParsed} — {t.previewing}
                </div>
                <Table variant="data">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t.columnSource}</TableHead>
                      <TableHead>{t.columnId}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parsedRows.slice(0, 10).map((row, i) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional and never reordered
                      <TableRow key={i}>
                        <TableCell className="font-mono text-xs">{row.source}</TableCell>
                        <TableCell className="font-mono text-xs">{row.id}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        {showProgress && job && (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">{t.progress}</span>
                <span className="text-muted-foreground">
                  {job.processedRows} / {job.totalRows} {t.rowsProcessed}
                </span>
              </div>
              <Progress value={job.totalRows ? (job.processedRows / job.totalRows) * 100 : 0} />
              <div className="flex gap-4 text-sm text-muted-foreground">
                <span>
                  {job.successRows} {t.successCount}
                </span>
                <span>
                  {job.failedRows} {t.failedCount}
                </span>
                {job.rows.some((r) => r.status === "skipped") && (
                  <span>
                    {job.rows.filter((r) => r.status === "skipped").length} {t.statusSkipped}
                  </span>
                )}
              </div>
            </div>

            {job.errorMessage && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {job.errorMessage}
              </div>
            )}

            <div className="space-y-2">
              <div className="text-sm font-medium">{t.perRowResults}</div>
              <div className="max-h-96 overflow-auto">
                <Table variant="data">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t.columnSource}</TableHead>
                      <TableHead>{t.columnId}</TableHead>
                      <TableHead>{t.columnStatus}</TableHead>
                      <TableHead>{t.columnError}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {job.rows.map((r, i) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional and never reordered
                      <TableRow key={i}>
                        <TableCell className="font-mono text-xs">{r.source}</TableCell>
                        <TableCell className="font-mono text-xs">{r.id}</TableCell>
                        <TableCell className="text-xs">{statusLabel(r.status)}</TableCell>
                        <TableCell
                          className={
                            r.status === "skipped"
                              ? "text-xs text-muted-foreground"
                              : "text-xs text-destructive"
                          }
                        >
                          {r.error ?? ""}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {!showProgress && (
            <>
              <DialogCancel>{t.cancel}</DialogCancel>
              <Button
                onClick={handleSubmit}
                disabled={parsedRows.length === 0 || !!parseError}
                loading={startImport.isPending}
              >
                {t.submit}
              </Button>
            </>
          )}
          {showProgress && (
            <Button onClick={handleClose} disabled={!finished}>
              {t.close}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
